// =============================================================================
// ui/shell/FeedbackModal.tsx — Send feedback.
// -----------------------------------------------------------------------------
// The only route a user has to tell us anything. Until this landed the new
// client had none at all — and after launch it is the client that ships (D56),
// so its absence would have meant a first-run user hitting a problem had no way
// to report it except finding an email address, which most people will not do.
// They will just stop using it, and that is the feedback you never hear.
//
// ── 🚨 WHAT IS ATTACHED, AND WHY IT IS SHOWN BEFORE SENDING ──
//
// The tab, the app version, the window size, the URL and the last few
// JavaScript errors go with every report. That is what turns "the chart went
// blank" into something fixable.
//
// It is also, unavoidably, information about the person. So the disclosure sits
// on the form, open-able before Send, listing exactly what will go — including
// the error COUNT, so nobody can be surprised by what was collected. Attaching
// diagnostics silently and mentioning it in a privacy policy is the version of
// this that is technically honest and actually isn't.
//
// ── ⚠ WHERE IT GOES AFTERWARDS IS NOT THIS SCREEN'S BUSINESS ──
//
// A bug or a feature request opens an issue on the private tracker; the other
// three go to the support inbox (D162). The client sends a type and the Edge
// Function routes it — so that policy can change without touching this file,
// which is the point of it living server-side.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSupabase } from '../state/supabase';
import {
  FEEDBACK_TYPES, gatherContext, sendFeedback,
  type FeedbackContext, type FeedbackTypeId,
} from '../../io/sync/feedback';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface FeedbackModalProps {
  /** The tab they were on. Goes in the report so triage starts in the right place. */
  currentTab: string;
  appVersion: string;
  onClose: () => void;
}

export function FeedbackModal({ currentTab, appVersion, onClose }: FeedbackModalProps) {
  const { sb } = useSupabase();
  const [type, setType] = useState<FeedbackTypeId>('bug');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  /**
   * 🚨 CAPTURED ONCE, WHEN THE MODAL OPENS — see `gatherContext`. The errors
   * that caused the bug are the ones already in the buffer; re-reading it at
   * submit time could let a noisy warning push the actual cause off a
   * ten-entry ring while the person was still typing.
   *
   * ⚠ And it is what the disclosure below shows, so the preview is the payload
   * rather than a description of one.
   */
  const ctx = useMemo<FeedbackContext>(
    () => gatherContext(currentTab, appVersion), [currentTab, appVersion],
  );

  const meta = FEEDBACK_TYPES.find((t) => t.id === type) ?? FEEDBACK_TYPES[0];

  const submit = useCallback(async () => {
    const text = description.trim();
    if (!text) {
      setError('Tell us a bit about it before sending.');
      return;
    }
    setBusy(true);
    setError(null);
    const r = await sendFeedback(sb, text, type, ctx).catch((e: unknown) => ({
      // ⚠ The lesson D140 paid for: a thrown fetch REJECTS rather than
      // returning, and without this the button sits on "Sending…" for ever
      // over feedback that never went.
      //
      // ⚠ Recording it cannot alter the report on screen: `ctx` was captured
      // when the modal opened. It lands in the buffer for the NEXT attempt,
      // which is the one that has a chance of arriving.
      ok: false as const,
      message: reportFailure('send feedback', e),
    }));
    setBusy(false);
    if (!r.ok) { setError(r.message); return; }
    setSent(r.message);
  }, [sb, description, type, ctx]);

  return createPortal(
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-label="Send feedback">
      <div className="modal fb-modal">
        <header className="modal-head">
          <b>Send feedback</b>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>×</button>
        </header>

        <div className="modal-body fb-body">
          {sent ? (
            // ⚠ A STATE, not a toast. The old client closes the modal and fires a
            // toast that fades in four seconds; a confirmation the user might
            // miss, for the one action where "did that send?" is the whole
            // question, is worth a screen of its own.
            <div className="fb-sent" role="status">
              <p><b>{sent}</b></p>
              <p className="muted">
                {type === 'bug' || type === 'feature_request'
                  ? 'It has gone straight onto our tracker, so it will not get lost in an inbox.'
                  : 'It has gone to our support inbox and we read every one.'}
              </p>
              <button type="button" className="btn primary" onClick={onClose}>Done</button>
            </div>
          ) : (
            <>
              <p className="muted fb-intro">
                Your direct line to us — bugs, feature ideas, gripes about how something
                feels, questions, anything. We read every one.
              </p>

              <label className="fb-field">
                <span className="fb-label">What kind of feedback?</span>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as FeedbackTypeId)}
                  disabled={busy}
                >
                  {FEEDBACK_TYPES.map((t) => (
                    <option key={t.id} value={t.id}>{t.icon}  {t.label}</option>
                  ))}
                </select>
              </label>

              <label className="fb-field">
                {/* ⚠ A real label, not just a placeholder. A placeholder vanishes
                    the moment someone starts typing, which is exactly when a
                    long-form box stops saying what it wants. */}
                <span className="fb-label">Tell us about it</span>
                <textarea
                  value={description}
                  onChange={(e) => { setDescription(e.target.value); setError(null); }}
                  placeholder={meta!.placeholder}
                  rows={6}
                  autoFocus
                  disabled={busy}
                />
              </label>

              {/* 🚨 CLOSED BY DEFAULT, BUT PRESENT AND OPENABLE BEFORE SEND. */}
              <details className="fb-ctx">
                <summary>What we’ll attach automatically</summary>
                <div className="fb-ctx-body">
                  <div><span>Tab</span> {ctx.currentTab}</div>
                  {/* ⚠ NO `v` PREFIX. The new client's version is a build id,
                      not a number — "vdev-34abaf2" reads like a typo. */}
                  <div><span>Version</span> {ctx.appVersion}</div>
                  <div><span>Window</span> {ctx.windowSize}</div>
                  <div className="fb-ctx-url"><span>URL</span> {ctx.pageUrl}</div>
                  <div className="fb-ctx-url"><span>Browser</span> {ctx.userAgent}</div>
                  <div>
                    <span>Recent errors</span> {ctx.recentErrors.length}
                    {ctx.recentErrors.length
                      ? ' — the last few things that went wrong in the app, with their stack traces'
                      : ' — nothing has gone wrong this session'}
                  </div>
                </div>
              </details>

              {error ? <p className="fb-error" role="alert">{error}</p> : null}
            </>
          )}
        </div>

        {sent ? null : (
          <footer className="modal-foot">
            <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
            <span className="grow" />
            <button
              type="button"
              className="btn primary"
              onClick={() => void submit()}
              disabled={busy || !description.trim()}
            >
              {busy ? 'Sending…' : 'Send'}
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
